'use client';

import React, { PropsWithChildren, ReactNode, useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaOptionsType } from 'embla-carousel';
import Image from 'next/image';

import s from './styles.module.scss';

import { items } from '../../../mocks/reviews';

const getInitials = (name: string) =>
    name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

const StarRating = ({ count = 0 }: { count?: number }) => {
    const stars = Array.from({ length: Math.max(0, count) });
    return (
        <div className={s.stars} aria-label={`Оцінка: ${count}`}>
            {stars.map((_, i) => (
                <Image key={i} src="/icons/star.svg" alt="зірка" width={16} height={16} />
            ))}
        </div>
    );
};

export type ReviewsControlsProps = {
    prevContent?: ReactNode;
    nextContent?: ReactNode;
    className?: string;
};

export type ReviewsProps = PropsWithChildren<{
    controls?: ReviewsControlsProps;
    options?: EmblaOptionsType;
}>;

const BP = 786;

export const Reviews = ({ controls, options }: ReviewsProps) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({
        align: 'start',
        dragFree: false,
        containScroll: 'trimSnaps',
        skipSnaps: false,
        slidesToScroll: 1,
        ...options,
    });

    const onPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
    const onNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

    const [mounted, setMounted] = useState(false);
    const [vw, setVw] = useState<number | null>(null);

    useEffect(() => {
        setMounted(true);

        const setWidth = () => setVw(window.innerWidth);
        setWidth();

        const mql = window.matchMedia(`(max-width:${BP}px)`);
        const onChange = () => setVw(window.innerWidth);

        if (mql.addEventListener) mql.addEventListener('change', onChange);
        else mql.addListener(onChange);

        window.addEventListener('resize', setWidth);

        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', onChange);
            else mql.removeListener(onChange);
            window.removeEventListener('resize', setWidth);
        };
    }, []);

    if (!mounted || vw === null) {
        return <div className={s.root} />;
    }

    const isMobile = vw <= BP;

    return (
        <section className={s.root}>
            {isMobile ? (
                <div className={s.title}>
                        <div className={s.title_mb}>
                           <div>
                               <p className={s.title_text_1}>Страшно?</p>
                               <p>Ось що кажуть</p>
                           </div>
                            <Image src="/images/reviews.png" alt="" width={153} height={153} />
                        </div>
                    <p style={{alignSelf: 'end'}}>про RaceCamp <br/><span>наші кемпери</span></p>
                </div>
            ) : (
                <div className={s.title}>
                    <div className={s.title_text}>
                        <p className={s.title_text_1}>Страшно?</p>
                        <p className={s.title_text_2}>
                            Ось що кажуть про RaceCamp <span>наші кемпери</span>
                        </p>
                    </div>
                    <Image src="/images/reviews.png" alt="" width={153} height={153} />
                </div>
            )}

            <div className={s.viewport} ref={emblaRef}>
                <div className={s.container}>
                    {items.map((r) => (
                        <article key={r.id} className={s.slide}>
                            <div className={s.card}>
                                <header className={s.cardHeader}>
                                    <div className={s.person}>
                                        <div className={s.avatar}>
                                            {r.avatar ? (
                                                <Image
                                                    src={r.avatar}
                                                    alt={r.name}
                                                    width={61}
                                                    height={61}
                                                    className={s.avatarImg}
                                                />
                                            ) : (
                                                <span className={s.initials}>{getInitials(r.name)}</span>
                                            )}
                                        </div>
                                        <div className={s.nameContainer}>
                                            <div className={s.name}>{r.name}</div>
                                            <StarRating count={r.stars} />
                                        </div>
                                    </div>
                                </header>
                                <p className={s.text}>{r.text}</p>
                            </div>
                        </article>
                    ))}
                </div>

                <div className={`${s.controls} ${controls?.className ?? ''}`.trim()}>
                    <button
                        type="button"
                        className={`${s.navBtn} ${s.prev}`}
                        onClick={onPrev}
                        aria-label="Попередній відгук"
                    >
                        {controls?.prevContent ?? (
                            <Image
                                className={s.icon}
                                src="/icons/arrow_slider.svg"
                                alt=""
                                aria-hidden
                                width={20}
                                height={20}
                            />
                        )}
                    </button>
                    <button
                        type="button"
                        className={`${s.navBtn} ${s.next}`}
                        onClick={onNext}
                        aria-label="Наступний відгук"
                    >
                        {controls?.nextContent ?? (
                            <Image
                                className={s.icon}
                                src="/icons/arrow_slider.svg"
                                alt=""
                                aria-hidden
                                width={20}
                                height={20}
                            />
                        )}
                    </button>
                </div>
            </div>

            <div className={s.desc}>
                <p>
                    *звiсно ми вiдредагували вiдгуки, лишивши тiльки головне, адже кожен з них це
                    лист з iнсайтами та подяками на 2-3 сторiнки
                </p>
            </div>
        </section>
    );
};
